import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useData } from "@/lib/store";
import { formatCents } from "@/lib/money";
import { Sparkles, Check, X, Mail, Calendar, MessageCircle, Zap, ExternalLink, Plug } from "lucide-react";
import { TYPE_META, cn } from "@/lib/utils";
import { toast } from "sonner";

const SOURCE_ICON = { email: Mail, calendar: Calendar, teams: MessageCircle };

export default function InboxPage() {
  const { refresh } = useData();
  const [connections, setConnections] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/connections"), api.get("/suggestions?status_=pending")]);
      setConnections(c.data); setSuggestions(s.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connectMock = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/connections/mock/connect");
      toast.success(`Mock provider connected · ${r.data.seeded_suggestions} suggestions imported`);
      await load();
    } catch { toast.error("Connection failed"); }
    finally { setConnecting(false); }
  };

  const disconnect = async (cid) => {
    if (!confirm("Disconnect this provider? Its suggestions will be purged.")) return;
    await api.delete(`/connections/${cid}`);
    toast.success("Disconnected");
    await load();
  };

  const accept = async (s) => {
    setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
    try {
      await api.post(`/suggestions/${s.id}/accept`, {});
      toast.success(`Filed as ${TYPE_META[s.kind]?.label || s.kind}`, { description: s.proposed_payload_json?.title });
      await refresh();
    } catch { toast.error("Accept failed"); load(); }
  };
  const dismiss = async (s) => {
    setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
    try { await api.post(`/suggestions/${s.id}/dismiss`); }
    catch { toast.error("Dismiss failed"); load(); }
  };

  return (
    <div className="animate-fade-in-up">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-medium text-surface-on tracking-tight">Triage inbox</h2>
          <p className="text-sm text-surface-onVariant mt-1">Everything your connected accounts turned up — approve in one tap.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {connections.map((c) => (
            <button
              key={c.id}
              data-testid={`connection-chip-${c.provider}`}
              onClick={() => disconnect(c.id)}
              className="inline-flex items-center gap-2 rounded-full bg-primary-container text-primary-onContainer text-xs font-medium px-3 py-1.5 hover:bg-danger-container hover:text-danger transition-colors duration-200 ease-md"
              title="Click to disconnect"
            >
              <Zap className="w-3 h-3" /> {c.provider}
              <span className="text-primary-onContainer/70">· {c.account_email}</span>
            </button>
          ))}
          <button
            data-testid="connect-mock-btn"
            onClick={connectMock}
            disabled={connecting || connections.some((c) => c.provider === "mock")}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md"
          >
            <Plug className="w-4 h-4" />
            {connecting ? "Connecting…" : connections.some((c) => c.provider === "mock") ? "Mock connected" : "Connect Mock provider"}
          </button>
        </div>
      </div>

      {connections.length === 0 && !loading && (
        <EmptyState onConnect={connectMock} connecting={connecting} />
      )}

      {loading && <div className="text-sm text-surface-onVariant">Loading…</div>}

      {!loading && connections.length > 0 && suggestions.length === 0 && (
        <div className="rounded-hero bg-surface-container p-16 text-center">
          <div className="text-4xl">🌿</div>
          <div className="text-lg font-medium text-surface-on mt-3">Inbox zero for your admin.</div>
          <div className="text-sm text-surface-onVariant mt-1">Nothing pending. New emails/events will land here automatically.</div>
        </div>
      )}

      {!loading && suggestions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="suggestions-grid">
          {suggestions.map((s) => (
            <SuggestionCard key={s.id} s={s} onAccept={() => accept(s)} onDismiss={() => dismiss(s)} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onConnect, connecting }) {
  return (
    <div className="relative overflow-hidden rounded-hero md:rounded-heroLg bg-surface-container p-10 md:p-16 text-center">
      <div className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-primary-container/60 blur-[80px] pointer-events-none" />
      <div className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full bg-tertiary-container/60 blur-[80px] pointer-events-none" />
      <div className="relative z-10 max-w-xl mx-auto">
        <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto">
          <Sparkles className="w-6 h-6" />
        </div>
        <h3 className="text-2xl font-medium text-surface-on mt-6 tracking-tight">Bring your inbox to Aevum</h3>
        <p className="text-sm text-surface-onVariant mt-2">
          Connect your email + calendar and Aevum will watch for anything actionable — bills, renewals, follow-ups — and drop them here as one-tap suggestions.
        </p>
        <p className="text-xs text-surface-onVariant mt-4">
          Real Google/Outlook integrations arrive next. For now try the Mock provider to see the flow.
        </p>
        <button
          onClick={onConnect}
          disabled={connecting}
          data-testid="empty-connect-mock"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-medium px-6 py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md"
        >
          <Plug className="w-4 h-4" />
          {connecting ? "Connecting…" : "Try Mock provider"}
        </button>
      </div>
    </div>
  );
}

function SuggestionCard({ s, onAccept, onDismiss }) {
  const p = s.proposed_payload_json || {};
  const Icon = SOURCE_ICON[s.source_type] || Mail;
  const kindMeta = TYPE_META[s.kind] || { label: s.kind, color: "bg-surface-containerHigh text-surface-onVariant" };
  const confidence = Math.round((s.confidence || 0) * 100);
  const isCalendarEvent = s.kind === "event";
  return (
    <div data-testid={`suggestion-${s.id}`} className="bg-surface-container rounded-hero p-5 border border-outline-variant hover:shadow-sm transition-[box-shadow] duration-200 ease-md">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-primary-container text-primary-onContainer flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-surface-onVariant flex items-center gap-2 flex-wrap">
            <span className="capitalize">{s.source_type}</span>
            <span className="w-1 h-1 rounded-full bg-surface-onVariant/50" />
            <span className="truncate">{s.source_summary}</span>
          </div>
          <div className="mt-2 text-lg font-medium text-surface-on leading-snug">
            {p.title || "Untitled"}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full", kindMeta.color)}>{isCalendarEvent ? "Event" : kindMeta.label}</span>
            {p.space && <span className="text-xs text-surface-onVariant">{p.space}</span>}
            {p.due_date && <span className="text-xs text-surface-onVariant">· due {p.due_date}</span>}
            {p.amount_cents && <span className="text-xs text-surface-on font-medium">· {formatCents(p.amount_cents)}</span>}
            <span className={cn(
              "text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full ml-auto",
              confidence >= 90 ? "bg-success-container text-success" :
              confidence >= 70 ? "bg-surface-containerHigh text-surface-onVariant" :
              "bg-danger-container text-danger"
            )}>{confidence}% conf</span>
          </div>
          <div className="mt-4 flex items-center gap-2">
            {!isCalendarEvent && (
              <button
                data-testid={`suggestion-accept-${s.id}`}
                onClick={onAccept}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md"
              ><Check className="w-4 h-4" /> Accept</button>
            )}
            {isCalendarEvent && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-surface text-surface-onVariant text-xs px-3 py-1.5 border border-outline-variant">
                <Calendar className="w-3 h-3" /> Mirrored — no action needed
              </span>
            )}
            <button
              data-testid={`suggestion-dismiss-${s.id}`}
              onClick={onDismiss}
              className="inline-flex items-center gap-1.5 rounded-full text-sm font-medium text-surface-on px-4 py-2 hover:bg-primary/8 transition-colors duration-200 ease-md"
            ><X className="w-4 h-4" /> Dismiss</button>
            <a
              href={s.source_ref}
              onClick={(e) => e.preventDefault()}
              title={s.source_ref}
              className="ml-auto text-xs text-surface-onVariant inline-flex items-center gap-1 hover:text-primary transition-colors"
            >Source <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      </div>
    </div>
  );
}
